/**
 * Link Document Dialog Component
 *
 * Allows users to link an existing audit document to a checklist item.
 */

import { useState, useMemo, useCallback } from "react";
import { FileText, Search, Loader2 } from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui";
import { useUnlinkedDocumentsForAudit } from "@/hooks/use-unlinked-documents-for-audit";
import { useLinkDocument } from "@/hooks/use-link-document";
import { useTranslation } from "@audit-app/utils";
import type { Id } from "@/packages/types";

interface LinkDocumentDialogProps {
  checklistItemId: Id<"checklistItems">;
  auditId: Id<"audits">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function LinkDocumentDialog({
  checklistItemId,
  auditId,
  open,
  onOpenChange,
}: LinkDocumentDialogProps) {
  const { t } = useTranslation(["audits"]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null,
  );
  const [isLinking, setIsLinking] = useState(false);

  const documents = useUnlinkedDocumentsForAudit(auditId, checklistItemId);
  const linkDocument = useLinkDocument();

  const filteredDocuments = useMemo(() => {
    if (!documents) return [];
    if (!searchQuery.trim()) return documents;
    const query = searchQuery.toLowerCase();
    return documents.filter((doc) =>
      doc.fileName.toLowerCase().includes(query),
    );
  }, [documents, searchQuery]);

  const handleLink = useCallback(async () => {
    if (!selectedDocumentId) return;

    setIsLinking(true);
    try {
      await linkDocument({
        documentId: selectedDocumentId as Id<"documents">,
        checklistItemId,
      });
      onOpenChange(false);
      setSelectedDocumentId(null);
      setSearchQuery("");
    } finally {
      setIsLinking(false);
    }
  }, [selectedDocumentId, checklistItemId, linkDocument, onOpenChange]);

  const handleClose = useCallback(() => {
    setSelectedDocumentId(null);
    setSearchQuery("");
    onOpenChange(false);
  }, [onOpenChange]);

  const isLoading = documents === undefined;
  const isEmpty = filteredDocuments.length === 0;
  const hasNoDocumentsInAudit =
    documents !== undefined && documents.length === 0;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-[60] bg-black/50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              />
            </Dialog.Overlay>

            <Dialog.Content
              asChild
              onPointerDownOutside={(e) => {
                // Prevent closing when clicking inside the dialog
                e.preventDefault();
              }}
            >
              <motion.div
                className="fixed inset-0 z-[60] flex items-center justify-center focus:outline-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.div
                  className="w-full max-w-[480px] rounded-[12px] bg-card shadow-xl"
                  initial={{ y: 24, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 24, opacity: 0 }}
                  transition={{
                    type: "spring",
                    damping: 25,
                    stiffness: 300,
                  }}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                    <Dialog.Title className="text-[15px] font-semibold text-primary">
                      {t("audits:checklist.linkDocumentDialog.title")}
                    </Dialog.Title>
                    <Dialog.Close asChild>
                      <button
                        type="button"
                        className="p-1 rounded-md hover:bg-card-hover transition-colors"
                        aria-label="Close dialog"
                      >
                        <svg
                          width="15"
                          height="15"
                          viewBox="0 0 15 15"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="text-muted"
                          aria-hidden="true"
                        >
                          <path
                            d="M11.7816 4.03157C12.0062 3.80702 12.0062 3.44295 11.7816 3.2184C11.5571 2.99385 11.193 2.99385 10.9685 3.2184L7.50005 6.68682L4.03164 3.2184C3.80708 2.99385 3.44301 2.99385 3.21846 3.2184C2.99391 3.44295 2.99391 3.80702 3.21846 4.03157L6.68688 7.49999L3.21846 10.9684C2.99391 11.193 2.99391 11.557 3.21846 11.7816C3.44301 12.0061 3.80708 12.0061 4.03164 11.7816L7.50005 8.31316L10.9685 11.7816C11.193 12.0061 11.5571 12.0061 11.7816 11.7816C12.0062 11.557 12.0062 11.193 11.7816 10.9684L8.31322 7.49999L11.7816 4.03157Z"
                            fill="currentColor"
                            fillRule="evenodd"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </Dialog.Close>
                  </div>

                  {/* Body */}
                  <div className="p-5">
                    {/* Search input */}
                    <div className="relative mb-4">
                      <Search
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"
                      />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t(
                          "audits:checklist.linkDocumentDialog.searchPlaceholder",
                        )}
                        className="w-full h-9 pl-9 pr-3 rounded-lg bg-card-[13px] border border-border text text-primary placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent transition-colors"
                      />
                    </div>

                    {/* Document list */}
                    <div className="max-h-[280px] overflow-y-auto">
                      {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2
                            size={20}
                            className="text-muted animate-spin"
                          />
                        </div>
                      ) : hasNoDocumentsInAudit ? (
                        <div className="text-center py-8">
                          <FileText
                            size={32}
                            className="mx-auto mb-3 text-muted/40"
                          />
                          <p className="text-[13px] font-medium text-secondary mb-1">
                            {t(
                              "audits:checklist.linkDocumentDialog.noDocumentsTitle",
                            )}
                          </p>
                          <p className="text-[12px] text-muted">
                            {t(
                              "audits:checklist.linkDocumentDialog.noDocumentsDescription",
                            )}
                          </p>
                        </div>
                      ) : isEmpty ? (
                        <div className="text-center py-8">
                          <FileText
                            size={32}
                            className="mx-auto mb-3 text-muted/40"
                          />
                          <p className="text-[13px] font-medium text-secondary mb-1">
                            {t(
                              "audits:checklist.linkDocumentDialog.allLinkedTitle",
                            )}
                          </p>
                          <p className="text-[12px] text-muted">
                            {t(
                              "audits:checklist.linkDocumentDialog.allLinkedDescription",
                            )}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {filteredDocuments.map((doc) => (
                            <button
                              key={doc.id}
                              type="button"
                              onClick={() => setSelectedDocumentId(doc.id)}
                              className={`w-full text-left flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors ${
                                selectedDocumentId === doc.id
                                  ? "bg-accent/10 border border-accent/30"
                                  : "hover:bg-card-hover border border-transparent"
                              }`}
                            >
                              <div
                                className={`rounded-md p-1.5 shrink-0 ${
                                  selectedDocumentId === doc.id
                                    ? "bg-accent/20"
                                    : "bg-accent/10"
                                }`}
                              >
                                <FileText
                                  size={16}
                                  className={
                                    selectedDocumentId === doc.id
                                      ? "text-accent"
                                      : "text-accent/70"
                                  }
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-[13px] font-medium text-primary truncate">
                                  {doc.fileName}
                                </p>
                                <p className="text-[11px] text-muted">
                                  {formatDate(doc.uploadedAt)}
                                </p>
                              </div>
                              {selectedDocumentId === doc.id && (
                                <div className="w-4 h-4 rounded-full bg-accent flex items-center justify-center shrink-0">
                                  <svg
                                    width="10"
                                    height="10"
                                    viewBox="0 0 10 10"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-hidden="true"
                                  >
                                    <path
                                      d="M8.33335 2.91669L3.75002 7.50002L1.66669 5.41669"
                                      stroke="white"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border">
                    <Button variant="secondary" onClick={handleClose}>
                      {t("audits:checklist.linkDocumentDialog.cancel")}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleLink}
                      disabled={
                        !selectedDocumentId ||
                        isLinking ||
                        hasNoDocumentsInAudit
                      }
                    >
                      {isLinking ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          {t("audits:checklist.linkDocumentDialog.linking")}
                        </>
                      ) : (
                        t("audits:checklist.linkDocumentDialog.linkDocument")
                      )}
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
